import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { RefreshCw, Loader2, Mail, BarChart2, Users, Package, TrendingUp, CheckCircle2, Activity } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('access_token') || sessionStorage.getItem('access_token');

const KPI = ({ label, value, sub, icon: Icon, color, bg }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-start gap-4">
    <div className={`w-11 h-11 rounded-xl ${bg} flex items-center justify-center flex-shrink-0`}>
      <Icon className={`h-5 w-5 ${color}`} />
    </div>
    <div>
      <p className="text-xs text-gray-500 font-medium">{label}</p>
      <p className="text-2xl font-black text-gray-900 mt-0.5">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  </div>
);

export default function SupplierAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchData = async () => {
    setLoading(true); setError('');
    try {
      const token = getToken();
      const { data: res } = await axios.get(`${API}/api/supplier/analytics`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load analytics.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
    </div>
  );

  if (error) return (
    <div className="flex items-center justify-center h-64 text-red-500 text-sm">{error}</div>
  );

  const maxCount = Math.max(...(data?.monthly_inquiries?.map(m => m.count) || [1]), 1);

  return (
    <div className="space-y-6 pb-10">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
          <p className="text-sm text-gray-500 mt-0.5">Your supplier performance at a glance</p>
        </div>
        <button onClick={fetchData} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-500 transition-colors">
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI label="Total Inquiries"        value={data?.total_inquiries ?? 0}       sub="All time" icon={Mail}        color="text-purple-600" bg="bg-purple-50" />
        <KPI label="Last 30 Days"           value={data?.recent_inquiries_30d ?? 0}  sub="New inquiries" icon={TrendingUp}   color="text-blue-600"   bg="bg-blue-50" />
        <KPI label="Proposals"              value={data?.total_proposals ?? 0}        sub={`${data?.acceptance_rate ?? 0}% accepted`} icon={CheckCircle2} color="text-green-600" bg="bg-green-50" />
        <KPI label="Catalog Categories"     value={data?.catalog_categories ?? 0}    sub={`${data?.catalog_items ?? 0} items`} icon={Package} color="text-amber-600" bg="bg-amber-50" />
        <KPI label="Profile Views"          value={data?.total_profile_views ?? 0}   sub="All time" icon={Activity}     color="text-pink-600"   bg="bg-pink-50" />
        <KPI label="Accepted Proposals"     value={data?.accepted_proposals ?? 0}    sub="Confirmed orders" icon={BarChart2} color="text-indigo-600" bg="bg-indigo-50" />
        <KPI label="Team Size"              value={data?.team_size ?? 0}             sub="Active members" icon={Users}    color="text-teal-600"   bg="bg-teal-50" />
        <KPI label="Acceptance Rate"        value={`${data?.acceptance_rate ?? 0}%`} sub="Proposal win rate" icon={TrendingUp} color="text-emerald-600" bg="bg-emerald-50" />
      </div>

      {/* Monthly Inquiries Chart */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <h2 className="text-base font-bold text-gray-900 mb-5">Monthly Inquiries</h2>
        {data?.monthly_inquiries?.length > 0 ? (
          <div className="flex items-end gap-3 h-40">
            {data.monthly_inquiries.map(({ month, count }) => (
              <div key={month} className="flex-1 flex flex-col items-center gap-1.5">
                <span className="text-xs font-bold text-gray-700">{count > 0 ? count : ''}</span>
                <div className="w-full bg-purple-100 rounded-t-lg overflow-hidden" style={{ height: '112px' }}>
                  <div
                    className="w-full bg-gradient-to-t from-purple-600 to-purple-400 rounded-t-lg transition-all duration-700"
                    style={{ height: `${Math.max((count / maxCount) * 112, count > 0 ? 4 : 0)}px` }}
                  />
                </div>
                <span className="text-[10px] text-gray-400 text-center leading-tight">{month}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex items-center justify-center h-40 text-gray-400 text-sm">No inquiry data yet</div>
        )}
      </div>

      {/* Quick Stats Row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          {
            title: 'Profile Visibility',
            value: data?.total_profile_views > 0 ? 'Indexed' : 'Awaiting Data',
            desc: 'Based on profile completion and activity',
            color: data?.total_profile_views > 0 ? 'text-green-600' : 'text-gray-400',
            bg: 'bg-green-50',
            border: 'border-green-100',
          },
          {
            title: 'Response Activity',
            value: data?.total_inquiries > 0 ? `${data?.total_inquiries} received` : 'No inquiries yet',
            desc: 'Total inquiries across all time',
            color: 'text-purple-600',
            bg: 'bg-purple-50',
            border: 'border-purple-100',
          },
          {
            title: 'Catalog Health',
            value: data?.catalog_categories > 0 ? `${data?.catalog_categories} categories` : 'Empty Catalog',
            desc: `${data?.catalog_items ?? 0} total items listed`,
            color: data?.catalog_categories > 0 ? 'text-amber-600' : 'text-gray-400',
            bg: 'bg-amber-50',
            border: 'border-amber-100',
          },
        ].map(({ title, value, desc, color, bg, border }) => (
          <div key={title} className={`rounded-2xl p-5 border ${border} ${bg}`}>
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">{title}</p>
            <p className={`text-lg font-black ${color}`}>{value}</p>
            <p className="text-xs text-gray-500 mt-1">{desc}</p>
          </div>
        ))}
      </div>

      {/* Tips */}
      <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl border border-purple-100 p-5">
        <p className="text-sm font-bold text-purple-800 mb-3">Tips to improve your stats</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {[
            { tip: 'Add more product categories to your catalog', done: (data?.catalog_categories || 0) > 3 },
            { tip: 'Complete your supplier profile (logo, tagline, about)', done: (data?.total_profile_views || 0) > 0 },
            { tip: 'Set coverage countries to improve match discovery', done: false },
            { tip: 'Respond to inquiries within 24 hours for higher acceptance', done: (data?.acceptance_rate || 0) >= 50 },
          ].map(({ tip, done }) => (
            <div key={tip} className="flex items-start gap-2">
              <CheckCircle2 className={`h-4 w-4 flex-shrink-0 mt-0.5 ${done ? 'text-green-500' : 'text-gray-300'}`} />
              <p className={`text-xs ${done ? 'text-gray-400 line-through' : 'text-purple-700'}`}>{tip}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
